import { yupResolver } from '@hookform/resolvers/yup';
import { Select } from 'antd';
import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { object, string } from 'yup';

import {
  Button,
  Field, //  Indicator,
  Input,
  Progress,
} from 'components/common';

import { useOrganization } from 'modules/organization/useOrganization';

import { useAdvertisersQuery } from '../../modules/apiData/dataApiSlice';
import { DSP_ADFORM, DSP_VISTAR, DSP_XANDR } from '../../utils/constants';
import { CURRENCY_OPTIONS, DEFAULT_CURRENCY } from '../../utils/currencyConstants';
import { AdvertiserFormInputs, CreateAdvertiserFormProps } from './agencySettingsTypes';

const MAX_LENGTH = 30;

const schema = object().shape({
  name: string().required('Advertiser name is required'),
  currency: string().trim(),
});

export const CreateAdvertiserForm = ({
  onClose,
  setCurrentStep,
  currentStep,
  agencyData,
  visible,
}: CreateAdvertiserFormProps) => {
  const [advertiserName, setAdvertiserName] = useState<string>('');
  const [dspId, setDspId] = useState<string>('');
  const [linkedAdvertiserId, setLinkedAdvertiserId] = useState<string>('');
  const [currency, setCurrency] = useState<string>(DEFAULT_CURRENCY);
  const {
    control,
    handleSubmit,
    watch,
    formState: { errors, isValid },
  } = useForm<AdvertiserFormInputs>({
    resolver: yupResolver(schema),
    defaultValues: {
      name: '',
      dspId: '',
      linkedAdvertiserId: '',
      currency: DEFAULT_CURRENCY,
    },
  });

  const { createAdvertiser, removeCreatedAgency, createdAgency, createAgency, dsp } =
    useOrganization();

  const handleFormSubmit = handleSubmit(({ name, dspId, linkedAdvertiserId, currency }) => {
    setAdvertiserName(name);
    setDspId(dspId ?? '');
    setLinkedAdvertiserId(linkedAdvertiserId ?? '');
    setCurrency(currency ?? DEFAULT_CURRENCY);
    createAgency(agencyData);
  });

  const { data: advertisers, isLoading } = useAdvertisersQuery(dsp ?? DSP_VISTAR);
  const { data: xandrAdvertisers, isLoading: isLoadingXandr } = useAdvertisersQuery(DSP_XANDR, {
    skip: dsp !== DSP_ADFORM,
  });

  useEffect(() => {
    if (createdAgency && advertiserName) {
      createAdvertiser({
        name: advertiserName,
        agencyId: createdAgency.id,
        dsp,
        dspId: dspId || '',
        linkedAdvertiserId: linkedAdvertiserId || undefined,
        currency: currency || DEFAULT_CURRENCY,
      });
      removeCreatedAgency();
      onClose();
    }
  }, [
    createdAgency,
    createAdvertiser,
    advertiserName,
    dspId,
    linkedAdvertiserId,
    currency,
    removeCreatedAgency,
    onClose,
    dsp,
  ]);

  return (
    <div
      className={classNames(
        { 'invisible absolute opacity-0 transition-visibility': !visible },
        'w-full',
      )}
    >
      <div className="self-stretch px-6 justify-start items-start gap-6 inline-flex ">
        <div className="grow shrink basis-0 py-5 flex-col justify-start items-start gap-2 inline-flex">
          <h1 className="self-stretch text-base-md font-medium leading-snug text-secondary-black">
            Create new advertiser
          </h1>
        </div>
      </div>
      <div className="px-6 pt-3 pb-6 gap-6 stretch-to-form">
        <div className="self-stretch flex-col justify-start items-start gap-1.5 flex">
          <form
            onSubmit={handleFormSubmit}
            className="w-full gap-4 flex flex-col justify-start items-start"
          >
            <Field
              label={'Advertiser name'}
              render={({ field, fieldState: { invalid } }) => (
                <Input
                  {...field}
                  invalid={invalid}
                  placeholder="Enter advertiser name..."
                  maxLength={MAX_LENGTH}
                />
              )}
              control={control}
              name={'name'}
              error={errors.name?.message}
              secondaryLabel={
                <div className="text-base-xs leading-tight font-medium flex items-center text-primary-gray-500">
                  {`${watch('name').length}/${MAX_LENGTH}`}
                </div>
              }
            />

            {dsp !== DSP_XANDR && (
              <Field
                label={'Linked advertiser'}
                render={({ field }) => (
                  <Select
                    {...field}
                    className={'w-full'}
                    value={field.value || undefined}
                    onChange={(value) => field.onChange(value)}
                    options={advertisers}
                    loading={isLoading}
                    disabled={isLoading}
                    showSearch
                    filterOption={(input, option) =>
                      (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
                    }
                    placeholder="Search and select linked advertiser..."
                  />
                )}
                control={control}
                name={'dspId'}
                error={errors.dspId?.message}
              />
            )}

            {dsp === DSP_ADFORM && (
              <Field
                label={'Linked Xandr advertiser (optional)'}
                render={({ field }) => (
                  <Select
                    {...field}
                    className={'w-full'}
                    value={field.value || undefined}
                    onChange={(value) => field.onChange(value)}
                    options={xandrAdvertisers}
                    loading={isLoadingXandr}
                    disabled={isLoadingXandr}
                    allowClear
                  />
                )}
                control={control}
                name={'linkedAdvertiserId'}
                error={errors.linkedAdvertiserId?.message}
              />
            )}

            <Field
              label={'Currency'}
              render={({ field }) => (
                <Select
                  {...field}
                  className={'w-full'}
                  placeholder="Select currency..."
                  options={CURRENCY_OPTIONS}
                />
              )}
              control={control}
              name={'currency'}
              error={errors.currency?.message}
            />
          </form>
          {/* <div className="stretch-to-form gap-3">
            {errorText && (
              <Indicator variant="danger" size="lg">
                {errorText}
              </Indicator>
            )}
          </div> */}
        </div>
      </div>
      <div className="self-stretch h-16 px-6 border-t border-primary-gray-100 justify-between items-center flex">
        <div className="items-center justify-between w-full flex">
          <Progress currentStep={currentStep} steps={2} />
          <div className="justify-end items-center gap-2 flex">
            <Button
              size="md"
              variant="secondary"
              onClick={() => setCurrentStep(1)}
              className="w-20"
            >
              Back
            </Button>
            <Button size="md" variant="primary" disabled={!isValid} onClick={handleFormSubmit}>
              Create
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
